import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { createContact, getContact, searchContacts } from "../api/appointmentsApi";

function contactLabel(contact) {
  if (!contact) return "";
  const name = contact.name || "Sin nombre";
  const phone = contact.phone ? ` - ${contact.phone}` : "";
  const contract = contact.contract_number ? ` - Contrato ${contact.contract_number}` : "";
  return `${name}${phone}${contract}`;
}

export default function ContactSelect({ value, onChange, label = "Paciente / cliente" }) {
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [newContact, setNewContact] = useState({
    name: "",
    phone: "",
    email: "",
    contract_number: "",
    notes: "",
  });

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError("");
        const data = await searchContacts({ q: query, limit: 25 });
        if (!cancelled) setContacts(data.rows || []);
      } catch (e) {
        if (!cancelled) {
          setContacts([]);
          setError(e.message || "No se pudo buscar contactos");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    async function loadSelected() {
      if (!value) return;
      if (contacts.some((c) => Number(c.id) === Number(value))) return;
      try {
        const contact = await getContact(value);
        if (!cancelled && contact?.id) {
          setContacts((prev) => [contact, ...prev]);
        }
      } catch {
        // Si el contacto ya no existe, dejamos el valor tal cual para no borrar datos.
      }
    }
    loadSelected();
    return () => {
      cancelled = true;
    };
  }, [value, contacts]);

  const selectedContact = useMemo(
    () => contacts.find((c) => Number(c.id) === Number(value)),
    [contacts, value]
  );

  function handleNewContactChange(e) {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  }

  async function handleCreateContact() {
    try {
      setCreating(true);
      setError("");
      const contact = await createContact({
        name: newContact.name.trim() || null,
        phone: newContact.phone.trim(),
        email: newContact.email.trim() || null,
        contract_number: newContact.contract_number.trim() || null,
        notes: newContact.notes.trim() || null,
      });
      if (!contact?.id) throw new Error("No se pudo crear el paciente");
      setContacts((prev) => [
        contact,
        ...prev.filter((c) => Number(c.id) !== Number(contact.id)),
      ]);
      onChange(Number(contact.id));
      setQuery(contact.name || contact.phone || "");
      setNewContact({
        name: "",
        phone: "",
        email: "",
        contract_number: "",
        notes: "",
      });
      setShowCreate(false);
    } catch (e2) {
      setError(e2.message || "No se pudo crear el paciente");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="appointment-contact-select">
      <label className="block text-xs font-semibold">{label}</label>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Buscar por nombre, telefono, email o contrato"
        className="border rounded px-2 py-1 text-sm w-full"
      />
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className="border rounded px-2 py-1 text-sm w-full mt-2"
      >
        <option value="">Selecciona un paciente</option>
        {contacts.map((contact) => (
          <option key={contact.id} value={contact.id}>
            {contactLabel(contact)}
          </option>
        ))}
      </select>
      <div className="appointment-field-hint">
        {loading
          ? "Buscando contactos..."
          : selectedContact
          ? `Seleccionado: ${contactLabel(selectedContact)}`
          : "Los datos salen de la tabla de contactos."}
      </div>
      <button
        type="button"
        className="appointment-inline-link"
        onClick={() => setShowCreate((open) => !open)}
      >
        {showCreate ? "Ocultar creación rápida" : "Crear paciente nuevo"}
      </button>
      {showCreate && (
        <div className="appointment-inline-create">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="block text-[9px] text-gray-400 mb-1">Nombre</label>
              <input
                name="name"
                value={newContact.name}
                onChange={handleNewContactChange}
                className="border rounded px-2 py-1 text-sm w-full"
                placeholder="Nombre del paciente"
              />
            </div>
            <div>
              <label className="block text-[9px] text-gray-400 mb-1">
                Teléfono *
              </label>
              <input
                name="phone"
                value={newContact.phone}
                onChange={handleNewContactChange}
                className="border rounded px-2 py-1 text-sm w-full"
                placeholder="0999999999"
                required
              />
            </div>
            <div>
              <label className="block text-[9px] text-gray-400 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={newContact.email}
                onChange={handleNewContactChange}
                className="border rounded px-2 py-1 text-sm w-full"
                placeholder="paciente@email.com"
              />
            </div>
            <div>
              <label className="block text-[9px] text-gray-400 mb-1">Contrato</label>
              <input
                name="contract_number"
                value={newContact.contract_number}
                onChange={handleNewContactChange}
                className="border rounded px-2 py-1 text-sm w-full"
                placeholder="Opcional"
              />
            </div>
          </div>
          <div>
            <label className="block text-[9px] text-gray-400 mb-1">Notas</label>
            <textarea
              name="notes"
              value={newContact.notes}
              onChange={handleNewContactChange}
              className="border rounded px-2 py-1 text-sm w-full"
              rows={2}
              placeholder="Alergias, referencia o comentario breve"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCreateContact}
              disabled={creating || !newContact.phone.trim()}
              className="btn !px-3 !py-1 text-xs disabled:opacity-60"
            >
              {creating ? "Creando..." : "Crear y seleccionar"}
            </button>
          </div>
        </div>
      )}
      {error && <div className="appointment-field-error">{error}</div>}
    </div>
  );
}

ContactSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};
